import React, { useEffect, useState } from 'react'
import {
  Create,
  required,
  SimpleForm,
  TextInput,
  useNotify,
  useRedirect,
  useTranslate,
  BooleanInput,
  NumberInput,
  FormDataConsumer,
  RadioButtonGroupInput
} from 'react-admin'
import { Grid, Hidden, Skeleton } from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { CardCrudBottomButtons } from 'src/components/uiElements/CRUD/CardCrudBottomButtons'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { productTypeServices } from 'src/services/reservation/product-type/product_type.service'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'
import { workUnitPaymentServices } from 'src/services/payment/workUnit/WorkUnit.service'
import { sourcePaymentServices } from 'src/services/payment/source/source.service'
import { companyServices } from 'src/services/payment/company/company.service'
import { cardServices } from 'src/services/payment/card/card.service'
import { DateTimePickers } from 'src/components/uiElements/react-hook-fields'
import { businessRuleServices } from 'src/services/sales/businessRule/businessRule.service'
import orderBy from 'lodash/orderBy'
import { commerceNumberServices } from 'src/services/payment/commerceNumber/commerceNumber.service'
import { useFormContext } from 'react-hook-form'
import { financialEntityServices } from 'src/services/payment/financialEntity/financialEntity.service'
import { gatewayServices } from 'src/services/payment/gateway/gateway.service'

const REQUIRED_FIELDS = [
  'product_id',
  'type_air',
  'work_unit_id',
  'source_id',
  'company_id',
  'card_id',
  'date_start',
  'date_validity'
]

const UpdateGateway = ({ currentGateway }) => {
  const { setValue } = useFormContext()

  useEffect(() => {
    setValue('gateway_id', currentGateway)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGateway])
}

const UpdateCardType = ({ currentCardType }) => {
  const { setValue } = useFormContext()

  useEffect(() => {
    setValue('type', currentCardType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCardType])
}

export const InstallmentCreate = (props) => {
  const translate = useTranslate()
  const notify = useNotify()
  const redirect = useRedirect()
  const [productTypeList, setProductTypeList] = useState([])
  const [loadingProductTypeList, setLoadingProductTypeList] = useState(false)
  const [workUnitList, setWorkUnitList] = useState([])
  const [loadingWorkUnitList, setLoadingWorkUnitList] = useState(false)
  const [sources, setSources] = useState([])
  const [loadingSources, setLoadingSources] = useState(false)
  const [companyList, setCompanyList] = useState([])
  const [loadingCompanyList, setLoadingCompanyList] = useState(false)
  const [cardList, setCardList] = useState([])
  const [loadingCardList, setLoadingCardList] = useState(false)
  const [gatewayList, setGatewayList] = useState([])
  const [allGatewayList, setAllGatewayList] = useState([])
  const [loadingGatewayList, setLoadingGatewayList] = useState(false)
  const [currentGateway, setCurrentGateway] = useState()
  const [airlineList, setAirlineList] = useState([])
  const [loadingAirlineList, setLoadingAirlineList] = useState(false)
  const [financialEntityList, setFinancialEntityList] = useState([])
  const [loadingFinancialEntityList, setLoadingFinancialEntityList] = useState(false)
  const [currentCardType, setCurrentCardType] = useState()

  useEffect(() => {
    getProductTypes()
    getSources()
    getWorkUnit()
    getCompany()
    getCard()
    getAirlines()
    getFinancialEntity()
    getGateway()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getProductTypes = async () => {
    try {
      setLoadingProductTypeList(true)
      const allProducts = await productTypeServices.getByParam()
      setProductTypeList(allProducts)
      setLoadingProductTypeList(false)
    } catch (error) {
      setProductTypeList([])
      setLoadingProductTypeList(false)
    }
  }

  const getSources = async () => {
    try {
      setLoadingSources(true)
      const response = await sourcePaymentServices.getByAll()
      setSources(response)
      setLoadingSources(false)
    } catch (error) {
      setSources([])
      setLoadingSources(false)
    }
  }

  const getWorkUnit = async () => {
    try {
      setLoadingWorkUnitList(true)
      const allWorkUnit = await workUnitPaymentServices.getByAll()
      setWorkUnitList(allWorkUnit)
      setLoadingWorkUnitList(false)
    } catch (error) {
      setWorkUnitList([])
      setLoadingWorkUnitList(false)
    }
  }

  const getCompany = async () => {
    try {
      setLoadingCompanyList(true)
      const work_unit = await companyServices.getByAll({ limit: 999 })
      setCompanyList(work_unit)
    } catch (error) {
      setCompanyList([])
    } finally {
      setLoadingCompanyList(false)
    }
  }

  const getCard = async () => {
    try {
      setLoadingCardList(true)
      const cards = await cardServices.getByAll()
      if (cards) {
        setCardList(cards)
        setLoadingCardList(false)
        return
      }
      notify(`card not found`, { type: 'error' })
      setLoadingCardList(false)
    } catch (error) {
      notify(`please, refresh page`, { type: 'error' })
      setLoadingCardList(false)
    }
  }

  const getGateway = async () => {
    try {
      const gateway = await gatewayServices.getByAll({ limit: 999 })
      setAllGatewayList(gateway)
    } catch (error) {
      setAllGatewayList([])
    }
  }

  const onlyUnique = (value, index, array) => {
    return array.findIndex(obj => obj.id === value.id) === index
  }

  const getGatewayByCommerceNumber = async (formData, work_unit_id__in, company_id, card_id) => {
    try {
      setLoadingGatewayList(true)
      const allCommerceNumber = await commerceNumberServices.getByAll({ work_unit_id__in, company_id, card_id })
      const allGateway = allCommerceNumber.map(elem => elem.gateway_relation)?.filter(onlyUnique) || []
      setGatewayList(allGateway)
      setLoadingGatewayList(false)

      if (allGateway.length === 1) {
        setCurrentGateway(allGateway[0].id)
      } else if (allGateway.length === 0 || !allGateway.find(elem => elem.id === formData.gateway_id)) {
        setCurrentGateway(null)

        if (allGateway.length === 0) {
          notify(translate('ra.notification.no_gateway_options'), { type: 'error' })
        }
      }
    } catch (error) {
      setGatewayList([])
      setLoadingGatewayList(false)
    }
  }

  const getAirlines = async () => {
    try {
      setLoadingAirlineList(true)
      const allAirlines = await businessRuleServices.getAirlines()
      setAirlineList(orderBy(allAirlines, ['long_name'], ['asc']))
      setLoadingAirlineList(false)
    } catch (error) {
      setAirlineList([])
      setLoadingAirlineList(false)
    }
  }

  const onChangeGatewayField = (formData, field, value) => {
    const formDataAux = {
      ...formData,
      [field]: value
    }
    if (formDataAux.work_unit_id && formDataAux.company_id && formDataAux.card_id && !Boolean(isDepositOrTransfer(formDataAux.card_id))) {
      getGatewayByCommerceNumber(formDataAux, formDataAux.work_unit_id, formDataAux.company_id, formDataAux.card_id)
    }
  }

  const onChangeCard = (value) => {
    const isDepositOrTransferAux = isDepositOrTransfer(value)
    if (Boolean(isDepositOrTransferAux)) {
      setCurrentCardType(null)
      setCurrentGateway(allGatewayList.find(elem =>
        elem.name === isDepositOrTransferAux.name ||
        elem.slug === isDepositOrTransferAux.slug
      )?.id)
    }
  }

  const getFinancialEntity = async () => {
    try {
      setLoadingFinancialEntityList(true)
      const allFinancialEntity = await financialEntityServices.getByAll()
      setFinancialEntityList(orderBy(allFinancialEntity, ['long_name'], ['asc']))
      setLoadingFinancialEntityList(false)
    } catch (error) {
      setFinancialEntityList([])
      setLoadingFinancialEntityList(false)
    }
  }

  const onSuccess = () => {
    notify('ra.notification.created_success_femenine', {
      type: 'success',
      messageArgs: { element: translate('ra.single_entity.installment') }
    })
    redirect('/installment')
  }

  const onError = () => {
    notify('ra.notification.created_error_femenine', {
      type: 'error',
      messageArgs: { element: translate('ra.single_entity.installment') }
    })
  }

  const isDepositOrTransfer = (cardId) => {
    const isDepositOrTransfer = cardList.find(elem => {
      return elem.id === cardId && (
        elem.name.toLowerCase() === 'deposito' || elem.slug === 'deposito' ||
        elem.name.toLowerCase() === 'transferencia' || elem.slug === 'transferencia' ||
        elem.name.toLowerCase() === 'debin' || elem.slug.toLowerCase() === 'debin'
      )
    })

    return isDepositOrTransfer
  }

  const transform = (data) => ({
    ...data,
    exclude_airline: data.exclude_airline.join(';')
  })

  return (
    <Create {...props} transform={transform} mutationOptions={{ onSuccess, onError }} actions={null}>
      <CardCommon title={translate('ra.title.create_installment')}>
        <SimpleForm toolbar={<CardCrudBottomButtons btnSave={true} />}>
          <UpdateGateway currentGateway={currentGateway} />
          <UpdateCardType currentCardType={currentCardType} />
          <Grid container spacing={3}>
            <EditInputsWrap label={translate('ra.field.product_type')} required>
              {loadingProductTypeList ? (
                <Skeleton variant='rectangular' width={'100%'} height={40} />
              ) : (
                <AutocompleteInput
                  source='product_id'
                  options={productTypeList}
                  validate={[required()]}
                  optionsText={['long_name']}
                  size='small'
                  disableClearable
                  useOptionValue
                  useOptionValueByOptions
                />
              )}
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.destination_type')} required>
              <RadioButtonGroupInput
                source='type_air'
                choices={[
                  { id: 'todos', name: translate('ra.destination_type.all') },
                  { id: 'nacional', name: translate('ra.destination_type.national') },
                  { id: 'internacional', name: translate('ra.destination_type.international') }
                ]}
                validate={[required()]}
                defaultValue={'todos'}
              />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.state')}>
              <BooleanInput label={translate('ra.field.is_active')} source='is_active' fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.work_unit')} required>
              <FormDataConsumer>
                {({ formData }) => {
                  return (
                    loadingWorkUnitList ? (
                      <Skeleton variant='rectangular' height={40} />
                    ) : (
                      <AutocompleteInput
                        source='work_unit_id'
                        options={workUnitList}
                        optionsText={['long_name']}
                        onChange={e => {
                          onChangeGatewayField(formData, 'work_unit_id', e.target.data)
                        }}
                        resetOnLoad={false}
                        validate={[required()]}
                        useOptionValue
                        useOptionValueByOptions
                      />
                    )
                  )
                }}
              </FormDataConsumer>
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.source')} required>
              {loadingSources ? (
                <Skeleton variant='rectangular' height={40} />
              ) : (
                <AutocompleteInput
                  source='source_id'
                  options={sources}
                  validate={[required()]}
                  optionsText={['long_name']}
                  disableClearable
                  useOptionValue
                  useOptionValueByOptions
                />
              )}
            </EditInputsWrap>
            <EditInputsWrap label={`${translate('ra.field.company')} (${translate('ra.field.business')})`} required >
              {loadingCompanyList ? (
                <Skeleton variant='rectangular' width={'100%'} height={40} />
              ) : (
                <FormDataConsumer>
                  {({ formData }) => {
                    return (
                      <AutocompleteInput
                        source='company_id'
                        options={companyList}
                        validate={[required()]}
                        optionsText={['short_name', 'long_name']}
                        separator=' | '
                        size='small'
                        disableClearable
                        useOptionValue
                        useOptionValueByOptions
                        onChange={e => onChangeGatewayField(formData, 'company_id', e.target.data)}
                      />
                    )
                  }}
                </FormDataConsumer>
              )}
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.card')} required>
              {loadingCardList ? (
                <Skeleton variant='rectangular' width={'100%'} height={40} />
              ) : (
                <FormDataConsumer>
                  {({ formData }) => {
                    return (
                      <AutocompleteInput
                        source='card_id'
                        options={cardList}
                        validate={[required()]}
                        optionsText={['long_name']}
                        size='small'
                        disableClearable
                        useOptionValue
                        useOptionValueByOptions
                        onChange={e => {
                          onChangeGatewayField(formData, 'card_id', e.target.data)
                          onChangeCard(e.target.data)
                        }}
                      />
                    )
                  }}
                </FormDataConsumer>
              )}
            </EditInputsWrap>
            <FormDataConsumer>
              {({ formData }) => (
                Boolean(isDepositOrTransfer(formData.card_id)) ? (
                  <Hidden smDown>
                    <Grid item md={8} />
                  </Hidden>
                ) : (
                  <>
                    <EditInputsWrap label={translate('ra.field.card_type')} required>
                      <SelectInput
                        size='small'
                        optionsText={['long_name']}
                        fullWidth
                        label={false}
                        source='type'
                        options={[
                          { id: 'credito', long_name: translate('ra.card_type.credito') },
                          { id: 'debito', long_name: translate('ra.card_type.debito') }
                        ]}
                        validate={[required()]}
                      />
                    </EditInputsWrap>
                    <EditInputsWrap
                      label={translate('ra.field.gateway')}
                      tooltiptext={translate('ra.tooltip.gateway_installment')}
                      required
                    >
                      {loadingGatewayList ? (
                        <Skeleton variant='rectangular' height={40} />
                      ) : (
                        <AutocompleteInput
                          source='gateway_id'
                          options={gatewayList}
                          optionsText={['name']}
                          resetOnLoad={false}
                          useOptionValue
                          useOptionValueByOptions
                          disabled={!formData.work_unit_id || !formData.company_id || !formData.card_id || gatewayList.length === 0}
                          onChange={e => setCurrentGateway(e.target.data)}
                          validate={[required()]}
                        />
                      )}
                    </EditInputsWrap>
                  </>
                )
              )}
            </FormDataConsumer>
            <EditInputsWrap
              label={`${translate('ra.field.site_label')} (${translate('ra.field.gds')}) (${translate('ra.message.optional')})`}
              tooltiptext={translate('ra.tooltip.site_label_installment')}
            >
              <TextInput source='gds' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap
              label={`${translate('ra.field.order')} (${translate('ra.message.optional')})`}
              tooltiptext={translate('ra.tooltip.order_installment')}
            >
              <NumberInput source='order' label={false} noDecimal />
            </EditInputsWrap>
            <Hidden smDown>
              <Grid item md={4} />
            </Hidden>
            <EditInputsWrap label={translate('ra.field.start_validity')} required>
              <DateTimePickers source='date_start' validate={[required()]} />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.end_validity')} required>
              <DateTimePickers source='date_validity' validate={[required()]} />
            </EditInputsWrap>
            <Hidden smDown>
              <Grid item md={4} />
            </Hidden>
            <EditInputsWrap
              label={`${translate('ra.field.include_route')} (${translate('ra.message.optional')})`}
              tooltiptext={translate('ra.tooltip.routes_installment')}
            >
              <TextInput source='include_route' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap
              label={`${translate('ra.field.exclude_route')} (${translate('ra.message.optional')})`}
              tooltiptext={translate('ra.tooltip.routes_installment')}
            >
              <TextInput source='exclude_route' label={false} fullWidth />
            </EditInputsWrap>
            <Hidden smDown>
              <Grid item md={4} />
            </Hidden>
            <EditInputsWrap
              label={`${translate('ra.field.exclude_airlines')} (${translate('ra.message.optional')})`}
              tooltiptext={translate('ra.tooltip.exclude_airlines_installment')}
            >
              {loadingAirlineList ? (
                <Skeleton variant='rectangular' width={'100%'} height={40} />
              ) : (
                <SelectInput
                  source='exclude_airline'
                  options={airlineList}
                  label=''
                  optionsText={['long_name']}
                  optionValue='short_name'
                  size='small'
                  multiple
                  showSearch
                  limitOptions={false}
                />
              )}
            </EditInputsWrap>
            <EditInputsWrap
              label={translate('ra.field.only_airline')}
              tooltiptext={translate('ra.tooltip.only_airline_installment')}
            >
              <BooleanInput label='' source='only_airline' fullWidth />
            </EditInputsWrap>
            <FormDataConsumer>
              {({ formData }) => {
                if (REQUIRED_FIELDS.every(field => formData[field])) {
                  return (
                    <Grid container item xs={12} className='installment-extra-data-container'>
                      <EditInputsWrap
                        label={translate('ra.field.bank')}
                        required
                      >
                        {loadingFinancialEntityList ? (
                          <Skeleton variant='rectangular' width={'100%'} height={40} />
                        ) : (
                          <AutocompleteInput
                            source='financial_entity_id'
                            options={financialEntityList}
                            validate={[required()]}
                            optionsText={['long_name']}
                            size='small'
                            disableClearable
                            useOptionValue
                            useOptionValueByOptions
                          />
                        )}
                      </EditInputsWrap>
                      <EditInputsWrap
                        label={translate('ra.field.installments')}
                        tooltiptext={translate('ra.tooltip.installments')}
                        required
                      >
                        <SelectInput
                          size='small'
                          optionsText={['id']}
                          fullWidth
                          label={false}
                          source='quota'
                          options={[...Array(12)].map((e, i) => ({ id: i + 1 }))}
                          validate={[required()]}
                        />
                      </EditInputsWrap>
                      <EditInputsWrap
                        label={translate('ra.field.installments_gateway')}
                        tooltiptext={translate('ra.tooltip.installments_gateway')}
                        required
                      >
                        <SelectInput
                          size='small'
                          optionsText={['id']}
                          fullWidth
                          label={false}
                          source='quota_gateway'
                          options={[...Array(24)].map((e, i) => ({ id: i + 1 }))}
                          validate={[required()]}
                        />
                      </EditInputsWrap>
                      <EditInputsWrap
                        label={translate('ra.field.coef')}
                        tooltiptext={translate('ra.tooltip.coef_installment')}
                        required
                      >
                        <NumberInput source='coef' label={false} fullWidth validate={[required()]} />
                      </EditInputsWrap>
                      <EditInputsWrap
                        label={translate('ra.field.expense')}
                        tooltiptext={translate('ra.tooltip.expense_installment')}
                        required
                      >
                        <NumberInput source='gasto' label={false} fullWidth validate={[required()]} />
                      </EditInputsWrap>
                      <EditInputsWrap
                        label={`${translate('ra.field.unifyCoef')} (unifycoef)`}
                        tooltiptext={translate('ra.tooltip.unify_coef_installment')}
                        required
                      >
                        <BooleanInput label='' source='unify_coef' fullWidth validate={[required()]} />
                      </EditInputsWrap>
                      <EditInputsWrap
                        label={`${translate('ra.field.bines')} (${translate('ra.message.optional')})`}
                        tooltiptext={translate('ra.tooltip.bines_installment')}
                        md={8}
                      >
                        <TextInput source='bines' label={false} fullWidth />
                      </EditInputsWrap>
                      <Hidden smDown>
                        <Grid item md={4} />
                      </Hidden>
                      <EditInputsWrap
                        label={`${translate('ra.field.cft')} (${translate('ra.message.optional')})`}
                        tooltiptext={translate('ra.tooltip.cft_installment')}
                      >
                        <TextInput source='cft' label={false} fullWidth />
                      </EditInputsWrap>
                      <EditInputsWrap
                        label={`${translate('ra.field.tea')} (${translate('ra.message.optional')})`}
                        tooltiptext={translate('ra.tooltip.tea_installment')}
                      >
                        <TextInput source='tea' label={false} fullWidth />
                      </EditInputsWrap>
                      <Hidden smDown>
                        <Grid item md={4} />
                      </Hidden>
                      <EditInputsWrap
                        label={`${translate('ra.field.refund')} (${translate('ra.message.optional')})`}
                        tooltiptext={translate('ra.tooltip.refund_installment')}
                      >
                        <TextInput source='refund' label={false} fullWidth />
                      </EditInputsWrap>
                      <EditInputsWrap
                        label={`${translate('ra.field.bonus')} (${translate('ra.message.optional')})`}
                      >
                        <TextInput source='bonus' label={false} fullWidth />
                      </EditInputsWrap>
                    </Grid>
                  )
                }
              }}
            </FormDataConsumer>
          </Grid>
        </SimpleForm>
      </CardCommon>
    </Create>
  )
}
